/**
 * Locking action hooks for document state management
 *
 * These hooks call direct named methods on the Zustand store (idiomatic Zustand pattern),
 * replacing the previous dispatch(actionCreator(...)) indirection.
 *
 * The store methods handle undo/redo, action records, and subscriber notifications internally.
 */
import {EditorModes} from "@buildwithflux/core";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import type {DocumentStore} from "modules/document/state/document";

/**
 * Hook to get the lockObjects action
 *
 * Calls the direct lockObjects method on the Zustand store.
 *
 * @returns A function to lock or unlock objects
 */
export function useLockObjects(): (
    objectUids: string[],
    mode: EditorModes.schematic | EditorModes.pcb,
    locked: boolean,
) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (objectUids: string[], mode: EditorModes.schematic | EditorModes.pcb, locked: boolean) => {
            documentStore.getState().lockObjects(objectUids, mode, locked);
        },
        [documentStore],
    );
}

/**
 * Factory function to create locking actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to perform locking actions.
 *
 * @param documentStore - The DocumentStore from FluxServices
 * @returns An object with locking action methods
 */
export function createLockingActions(documentStore: DocumentStore) {
    return {
        lockObjects: (objectUids: string[], mode: EditorModes.schematic | EditorModes.pcb, locked: boolean) => {
            documentStore.getState().lockObjects(objectUids, mode, locked);
        },
    };
}
